import { CREATABLE_METHOD_KINDS } from '@didto/domain';
import type { PaymentMethodKind } from '@didto/domain';
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Sheet,
  Toggle,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useSession } from '@/app/session';
import { useCommand } from '@/app/useCommand';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { METHOD_KIND_LABEL, fetchPaymentMethods, paymentKeys } from '@/lib/payments';
import type { PaymentMethod } from '@/lib/payments';
import { LEDGER_ROW_TONE } from '@/lib/status';
import './settings.css';

/** Payment methods (business-wide). Owner decision 5: only Cash and GCash are seeded. */
export function PaymentMethodsPage() {
  const s = useSession();
  const canManage = s.can('settings.manage');
  // ADR-037: every change here is a command; none starts while the server cannot be reached
  const online = useOnline();
  // D5 (ADR-032): a reference format is the business owner's own, from their signed-in account
  const isOwnerAccount =
    s.me?.principal === 'human' &&
    !!s.me.employee &&
    (s.me.owner_employee_ids ?? []).includes(s.me.employee.id);
  const cmd = useCommand([[...paymentKeys.methods]]);
  const [editing, setEditing] = useState<PaymentMethod | 'new' | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const methods = useQuery({ queryKey: paymentKeys.methods, queryFn: fetchPaymentMethods });
  const visible = (methods.data ?? []).filter((m) => showArchived || !m.archived_at);

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Settings"
        title="Payment methods"
        subtitle="How customers pay. A method that needs a reference asks the cashier for one at the till."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setEditing('new')} data-testid="new-method">
              New method
            </Button>
          ) : undefined
        }
      />
      <div className="d-spread">
        <Checkbox checked={showArchived} onChange={setShowArchived} label="Show archived" />
      </div>
      <div className="d-list" data-testid="method-list">
        {methods.isLoading && <Loading rows={3} label="Loading payment methods…" />}
        {methods.isError && (
          <ErrorState
            title="Could not load the payment methods"
            message={errorText(methods.error)}
            onRetry={() => void methods.refetch()}
          />
        )}
        {visible.map((m) => (
          <div className="d-row" key={m.id} data-testid={`method-${m.id}`}>
            <div className="d-row__main">
              <div className="d-row__title" id={`method-${m.id}-name`}>
                {m.name}
              </div>
              <div className="d-row__meta d-inline">
                <Badge>{METHOD_KIND_LABEL[m.kind]}</Badge>
                {m.requires_reference && <Badge tone="info">reference</Badge>}
                {m.reference_pattern && <Badge tone="info">format</Badge>}
                {m.opens_cash_drawer && <Badge outline>drawer</Badge>}
                {m.allowed_offline && <Badge tone="success">offline</Badge>}
                {m.archived_at ? (
                  <Badge tone={LEDGER_ROW_TONE.archived}>archived</Badge>
                ) : (
                  !m.enabled && <Badge tone="warning">disabled</Badge>
                )}
              </div>
            </div>
            {canManage && !m.archived_at && (
              <div className="d-actions">
                <Toggle
                  checked={m.enabled}
                  label={m.enabled ? 'Enabled' : 'Disabled'}
                  disabled={!online}
                  describedBy={`method-${m.id}-name`}
                  onChange={(v) =>
                    void cmd.mutateAsync({
                      kind: 'payment_method.update',
                      payload: { payment_method_id: m.id, enabled: v },
                    })
                  }
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(m)}
                  aria-label={`edit ${m.name}`}
                >
                  Edit
                </Button>
              </div>
            )}
            {canManage && m.archived_at && (
              <div className="d-actions">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!online}
                  onClick={() =>
                    void cmd.mutateAsync({
                      kind: 'payment_method.restore',
                      payload: { payment_method_id: m.id },
                    })
                  }
                >
                  Restore
                </Button>
              </div>
            )}
          </div>
        ))}
        {methods.isSuccess && visible.length === 0 && (
          <EmptyState title="No payment methods here" />
        )}
      </div>
      {editing && (
        <MethodSheet
          method={editing === 'new' ? null : editing}
          canSetFormat={isOwnerAccount}
          onSetFormat={async (pattern) => {
            if (editing === 'new') return;
            await cmd.mutateAsync({
              kind: 'payment_method.set_reference_format',
              payload: { payment_method_id: editing.id, pattern },
            });
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            if (editing === 'new')
              await cmd.mutateAsync({ kind: 'payment_method.create', payload: values });
            else
              await cmd.mutateAsync({
                kind: 'payment_method.update',
                payload: { payment_method_id: editing.id, ...values },
              });
            setEditing(null);
          }}
          onArchive={
            editing !== 'new'
              ? async () => {
                  await cmd.mutateAsync({
                    kind: 'payment_method.archive',
                    payload: { payment_method_id: editing.id },
                  });
                  setEditing(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

interface MethodValues {
  name: string;
  kind: Exclude<PaymentMethodKind, 'online'>;
  requires_reference: boolean;
  opens_cash_drawer: boolean;
  allowed_offline: boolean;
}

function MethodSheet({
  method,
  canSetFormat,
  onSetFormat,
  onClose,
  onSubmit,
  onArchive,
}: {
  method: PaymentMethod | null;
  canSetFormat: boolean;
  onSetFormat: (pattern: string | null) => Promise<void>;
  onClose: () => void;
  onSubmit: (values: MethodValues) => Promise<void>;
  onArchive?: () => Promise<void>;
}) {
  const online = useOnline();
  const [name, setName] = useState(method?.name ?? '');
  const [kind, setKind] = useState<MethodValues['kind']>(
    method && method.kind !== 'online' ? method.kind : 'ewallet',
  );
  const [requiresReference, setRequiresReference] = useState(method?.requires_reference ?? true);
  const [opensDrawer, setOpensDrawer] = useState(method?.opens_cash_drawer ?? false);
  const [allowedOffline, setAllowedOffline] = useState(method?.allowed_offline ?? false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit({
        name: name.trim(),
        kind,
        requires_reference: requiresReference,
        opens_cash_drawer: opensDrawer,
        allowed_offline: allowedOffline,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open onClose={onClose} title={method ? method.name : 'New payment method'}>
      <form className="d-stack" onSubmit={(e) => void submit(e)}>
        <Field label="Name">
          {(id) => (
            <Input
              id={id}
              value={name}
              maxLength={40}
              required
              onChange={(e) => setName(e.target.value)}
              data-testid="method-name"
            />
          )}
        </Field>
        {!method && (
          <Field label="Kind">
            {(id) => (
              <Select
                id={id}
                value={kind}
                onChange={(e) => setKind(e.target.value as MethodValues['kind'])}
                data-testid="method-kind"
              >
                {CREATABLE_METHOD_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {METHOD_KIND_LABEL[k]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <div className="d-stack d-stack--tight">
          <Toggle
            checked={requiresReference}
            onChange={setRequiresReference}
            label="Cashier must record a reference number"
          />
          <Toggle
            checked={opensDrawer}
            onChange={setOpensDrawer}
            label="Opens the cash drawer (once a drawer is connected)"
          />
          <Toggle
            checked={allowedOffline}
            onChange={setAllowedOffline}
            label="May be taken while offline (not used yet)"
          />
        </div>
        {method && method.requires_reference && (
          <ReferenceFormat method={method} canSet={canSetFormat} onSet={onSetFormat} />
        )}
        <div className="d-spread">
          {onArchive ? (
            <Button
              variant="danger"
              disabled={!online}
              onClick={() => void onArchive()}
              data-testid="archive-method"
            >
              Archive
            </Button>
          ) : (
            <span />
          )}
          <div className="d-actions">
            <Button variant="quiet" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={!online || !name.trim()}
              data-testid="save-method"
            >
              Save
            </Button>
          </div>
        </div>
      </form>
    </Sheet>
  );
}

/**
 * The reference format of a method (D5, ADR-032): none by default, so any reference is taken. The owner sets it once a
 * real receipt confirms the format; a reference that does not match is then refused at the counter. Whitespace is
 * removed before the check and leading zeroes are kept: a reference is a string, never a number.
 */
function ReferenceFormat({
  method,
  canSet,
  onSet,
}: {
  method: PaymentMethod;
  canSet: boolean;
  onSet: (pattern: string | null) => Promise<void>;
}) {
  const online = useOnline();
  const [pattern, setPattern] = useState(method.reference_pattern ?? '');
  const [digits, setDigits] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (value: string | null) => {
    setBusy(true);
    try {
      await onSet(value);
    } finally {
      setBusy(false);
    }
  };
  if (!canSet)
    return (
      <p className="d-help" data-testid="reference-format-readonly">
        Reference format:{' '}
        {method.reference_pattern ? (
          <span className="d-mono">{method.reference_pattern}</span>
        ) : (
          'none (any reference is taken)'
        )}
        . Set by the owner.
      </p>
    );
  const n = Number(digits);
  return (
    <section className="d-stack d-stack--tight" data-testid="reference-format">
      <hr className="d-divider" />
      <h3 className="d-eyebrow">Reference format (owner)</h3>
      <p className="d-help">
        Leave empty until a real receipt confirms the format. Once set, a reference that does not
        match is refused at the counter. Spaces are removed first; leading zeroes are kept.
      </p>
      <div className="methods__digits">
        <Field label="Digits only, exactly">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              value={digits}
              onChange={(e) => setDigits(e.target.value.replace(/\D/g, '').slice(0, 2))}
              className="d-input--num"
              data-testid="reference-format-digits"
            />
          )}
        </Field>
        <Button
          variant="ghost"
          size="sm"
          disabled={!(n >= 1 && n <= 64)}
          onClick={() => setPattern(`^[0-9]{${n}}$`)}
        >
          Use
        </Button>
      </div>
      <Field label="Pattern" help="A regular expression; for example ^[0-9]{13}$ is 13 digits.">
        {(id) => (
          <Input
            id={id}
            className="d-mono"
            value={pattern}
            maxLength={200}
            onChange={(e) => setPattern(e.target.value)}
            data-testid="reference-format-pattern"
          />
        )}
      </Field>
      <div className="d-sheet__actions">
        {method.reference_pattern && (
          <Button variant="quiet" busy={busy} disabled={!online} onClick={() => void save(null)}>
            Clear format
          </Button>
        )}
        <Button
          variant="primary"
          busy={busy}
          disabled={
            !online || !pattern.trim() || pattern.trim() === (method.reference_pattern ?? '')
          }
          onClick={() => void save(pattern.trim())}
          data-testid="reference-format-save"
        >
          Save format
        </Button>
      </div>
    </section>
  );
}
